import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  company_name: "",
  full_name: "",
  business_email: "",
  phone: "",
  country: "",
  city: "",
  headcount: "",
  current_method: "",
  problem: "",
  need: "",
};

const headcountOptions = ["1–20", "21–50", "51–200", "201–500", "500+"];
const methodOptions = [
  "In-house fleet",
  "Single transport provider",
  "Multiple transport providers",
  "Ride-hailing / taxis",
  "Employee self-arranged",
  "No formal arrangement yet",
];
const needOptions = ["Quote", "Consultation", "Transport Solution"];

const inputCls =
  "h-11 rounded-lg border-stone-200 bg-white focus-visible:ring-2 focus-visible:ring-orange-600 focus-visible:ring-offset-0";

const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-medium tracking-[0.08em] uppercase text-stone-500">
      {label}
    </Label>
    {children}
  </div>
);

const LeadForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@cafetransport.pro.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="assessment-form" data-testid="lead-form-section" className="py-24 md:py-32 bg-white border-t border-stone-200">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-orange-600 mb-4">
            Get started
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900">
            Request a Corporate Transport Assessment
          </h2>
          <p className="mt-4 text-base text-stone-600 leading-relaxed">
            Free initial assessment &bull; No obligation
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-12 bg-stone-50 rounded-2xl border border-stone-200 p-6 sm:p-10 shadow-sm"
        >
          {submitted ? (
            <div data-testid="lead-form-success" className="py-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-orange-600 mx-auto" strokeWidth={1.5} />
              <h3 className="mt-6 text-xl font-semibold text-stone-900">Request received.</h3>
              <p className="mt-2 text-base text-stone-600">
                We&rsquo;ll review your requirements and contact you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="lead-form" className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field id="company_name" label="Company Name">
                <Input id="company_name" data-testid="input-company-name" required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder="Acme Ltd." />
              </Field>
              <Field id="full_name" label="Full Name">
                <Input id="full_name" data-testid="input-full-name" required value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Jane Cooper" />
              </Field>
              <Field id="business_email" label="Business Email">
                <Input id="business_email" data-testid="input-business-email" type="email" required value={form.business_email} onChange={set("business_email")} className={inputCls} placeholder="jane@company.com" />
              </Field>
              <Field id="phone" label="Phone / WhatsApp">
                <Input id="phone" data-testid="input-phone" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+1 555 000 1234" />
              </Field>
              <Field id="country" label="Country">
                <Input id="country" data-testid="input-country" required value={form.country} onChange={set("country")} className={inputCls} placeholder="United States" />
              </Field>
              <Field id="city" label="City">
                <Input id="city" data-testid="input-city" required value={form.city} onChange={set("city")} className={inputCls} placeholder="Austin" />
              </Field>
              <Field id="headcount" label="Number of Employees / Passengers">
                <Select required value={form.headcount} onValueChange={set("headcount")}>
                  <SelectTrigger id="headcount" data-testid="select-headcount" className={inputCls}>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {headcountOptions.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`headcount-option-${o}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="current_method" label="Current Transportation Method">
                <Select required value={form.current_method} onValueChange={set("current_method")}>
                  <SelectTrigger id="current_method" data-testid="select-current-method" className={inputCls}>
                    <SelectValue placeholder="Select current setup" />
                  </SelectTrigger>
                  <SelectContent>
                    {methodOptions.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Field id="problem" label="Main Transportation Problem">
                  <Textarea
                    id="problem"
                    data-testid="textarea-problem"
                    required
                    rows={5}
                    value={form.problem}
                    onChange={set("problem")}
                    className="rounded-lg border-stone-200 bg-white focus-visible:ring-2 focus-visible:ring-orange-600 focus-visible:ring-offset-0 resize-y"
                    placeholder="Describe what's making transportation difficult for your business — routes, costs, reliability, scheduling, providers…"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field id="need" label="What do you need?">
                  <Select required value={form.need} onValueChange={set("need")}>
                    <SelectTrigger id="need" data-testid="select-need" className={inputCls}>
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent>
                      {needOptions.map((o) => (
                        <SelectItem key={o} value={o} data-testid={`need-option-${o.toLowerCase().replace(" ", "-")}`}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="sm:col-span-2 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-form-submit-button"
                  className="w-full rounded-full bg-orange-600 px-8 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-orange-700 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed transition-[background-color,box-shadow,opacity] duration-200 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? "Submitting…" : "Submit Request"}
                </button>
                <p className="mt-4 text-xs text-stone-500 text-center">
                  Your details are only used to respond to this inquiry.
                </p>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default LeadForm;
